import { useRef, useState } from 'react';
import type { JSX } from 'react';

import type {
  RemainingEffortReport,
  RemainingEffortReportInput,
  RestaufwandArbeitspaket,
  RestaufwandProjekt,
} from '@asas/domain';
import { compareIsoWeeks, currentIsoWeek, FACHLICHE_ZEITZONE } from '@asas/domain/iso-week';
import { aktuelleMeldung } from '@asas/domain/restaufwand';

import { anmeldeUrlMitRueckkehr, erzeugeEntwurfsspeicher } from '../daten/entwurf';
import { klassifiziereFehler } from '../daten/fehler';
import type { FehlerBefund } from '../daten/fehler';
import { entitaetsSchluessel, geteilterSerialisierer } from '../daten/serialisierung';
import { trpc, trpcRestaufwandGrund } from '../trpc';

import {
  ABWEISUNG_TEXT,
  hinweise,
  leereEingabe,
  projekteGefiltert,
  pruefeMeldeEntwurf,
  wochenAuswahl,
  zurMeldung,
} from './restaufwand';
import type { MeldeEingabe } from './restaufwand';
import {
  ArbeitspaketListe,
  Historie,
  MeldeFormular,
  ProjektAuswahl,
  RestaufwandKopfzeile,
} from './RestaufwandAnsicht';

export interface RestaufwandBereichProps {
  /** Die Kontokennung der angemeldeten Person — Inhaber eines Entwurfs (AC-016). */
  readonly inhaber: string;
  /**
   * Hat das Konto `remaining_effort:report_any` (AC-107-1)? Nur für die
   * Leermeldung; den Umfang prüft der Server.
   */
  readonly mandantenweit?: boolean;
}

/** Wohin nach der Neuanmeldung? Mit oder ohne gesicherte Eingabe. */
type Anmeldung = 'keine' | 'mitEntwurf' | 'ohneEntwurf';

/** Eine neue Meldungs-Id (E12): sie bleibt, bis die Meldung gespeichert ist. */
function neueId(): string {
  return crypto.randomUUID();
}

/**
 * Restaufwand und Fertigstellungsgrad melden (AC-601).
 *
 * Nach dem Rezept aus AC-016: Schreibzugriffe ueber die GETEILTE Warteschlange
 * je Arbeitspaket, jeder Fehler durch `klassifiziereFehler`, nach Erfolg gilt
 * die SERVERANTWORT.
 *
 * WIEDERHOLUNGSSICHER (E12): Die Id einer Meldung entsteht beim Oeffnen des
 * Formulars und bleibt, bis die Meldung gespeichert ist. Ist der Ausgang
 * ungewiss (Netz, Server), legt ein erneutes Speichern mit unveraenderter
 * Eingabe nichts doppelt an — der Server liefert die vorhandene Zeile.
 *
 * GELADEN WIRD ERST AUF ANFORDERUNG (Klick 1) — kein Effekt in dieser Datei.
 * Nach einer Ablehnung (403) bleibt das Melden bis zum Neuladen der Seite
 * gesperrt: jeder weitere Versuch schriebe eine Audit-Zeile.
 */
export function RestaufwandBereich({
  inhaber,
  mandantenweit = false,
}: RestaufwandBereichProps): JSX.Element {
  const heute = currentIsoWeek(new Date(), FACHLICHE_ZEITZONE);
  const wochen = wochenAuswahl(heute);

  const [projekte, setProjekte] = useState<RestaufwandProjekt[] | null>(null);
  // AC-107-1 (E3): ob das eigene Konto mit einer Person verknüpft ist — nur ob, nie wer.
  const [mitarbeiterVerknuepft, setMitarbeiterVerknuepft] = useState(true);
  const [laedt, setLaedt] = useState(false);
  const [ladefehler, setLadefehler] = useState<FehlerBefund | null>(null);
  const [suche, setSuche] = useState('');
  const [projekt, setProjekt] = useState<RestaufwandProjekt | null>(null);
  const [pakete, setPakete] = useState<RestaufwandArbeitspaket[] | null>(null);
  const [paket, setPaket] = useState<RestaufwandArbeitspaket | null>(null);
  const [historie, setHistorie] = useState<RemainingEffortReport[] | null>(null);
  const [auswahlLaedt, setAuswahlLaedt] = useState(false);
  const [auswahlFehler, setAuswahlFehler] = useState<FehlerBefund | null>(null);
  const [eingabe, setEingabe] = useState<MeldeEingabe>(() => leereEingabe(heute));
  const [rueckfrage, setRueckfrage] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [anmeldung, setAnmeldung] = useState<Anmeldung>('keine');
  const [zugriffVerweigert, setZugriffVerweigert] = useState(false);
  const meldungsId = useRef<string>(neueId());
  // Welche Auswahl gerade laedt. Eine Antwort fuer eine aeltere wird verworfen.
  const laedtFuer = useRef<string | null>(null);

  const entwuerfe = erzeugeEntwurfsspeicher({ inhaber });
  const anmeldeUrl = anmeldeUrlMitRueckkehr(
    typeof window === 'undefined' ? '/' : window.location.pathname,
  );
  // Nach einem 403 aus IRGENDEINER der vier Prozeduren bleibt alles gesperrt:
  // sie haengen am selben Recht, und jeder weitere Versuch schriebe eine
  // Audit-Zeile (Review PR #84, B3).
  const zugriffGesperrt =
    zugriffVerweigert || ladefehler?.art === 'zugriff' || auswahlFehler?.art === 'zugriff';
  const gesperrt = laedt || auswahlLaedt || sendet || zugriffGesperrt;

  function entwurfsSchluessel(workPackageId: string): string {
    return entitaetsSchluessel('restaufwand-formular', workPackageId);
  }

  function hinweiseZuruecksetzen(): void {
    setMeldung(null);
    setFehler(null);
    setAnmeldung('keine');
    setRueckfrage(false);
  }

  /** Klick 1. */
  async function oeffnen(): Promise<void> {
    setLaedt(true);
    setLadefehler(null);

    try {
      const uebersicht = await trpc.restaufwand.projekte.query();
      setProjekte(uebersicht.projekte);
      setMitarbeiterVerknuepft(uebersicht.mitarbeiterVerknuepft);
    } catch (error) {
      setLadefehler(klassifiziereFehler(error, { schreibzugriff: false }));
    } finally {
      setLaedt(false);
    }
  }

  function ladefehlerZeigen(error: unknown): void {
    const befund = klassifiziereFehler(error, { schreibzugriff: false });
    if (befund.art !== 'abgebrochen') setAuswahlFehler(befund);
  }

  /** Klick 2. */
  async function projektWaehlen(gewaehlt: RestaufwandProjekt): Promise<void> {
    laedtFuer.current = gewaehlt.id;
    setProjekt(gewaehlt);
    setPakete(null);
    setPaket(null);
    setHistorie(null);
    setAuswahlFehler(null);
    setAuswahlLaedt(true);
    hinweiseZuruecksetzen();

    try {
      const liste = await trpc.restaufwand.arbeitspakete.query({ projectId: gewaehlt.id });
      if (laedtFuer.current === gewaehlt.id) setPakete(liste);
    } catch (error) {
      if (laedtFuer.current === gewaehlt.id) ladefehlerZeigen(error);
    } finally {
      if (laedtFuer.current === gewaehlt.id) setAuswahlLaedt(false);
    }
  }

  /** Klick 3: Formular sofort, Historie laedt dazu. Ein Entwurf wird angeboten, nie abgeschickt. */
  async function paketWaehlen(gewaehlt: RestaufwandArbeitspaket): Promise<void> {
    laedtFuer.current = gewaehlt.id;
    setPaket(gewaehlt);
    setHistorie(null);
    setAuswahlFehler(null);
    hinweiseZuruecksetzen();
    meldungsId.current = neueId();

    const entwurf = entwuerfe.holen(entwurfsSchluessel(gewaehlt.id), pruefeMeldeEntwurf);
    if (entwurf === null) {
      setEingabe(leereEingabe(heute));
    } else {
      setEingabe(entwurf.eingabe);
      setMeldung(
        'Ihre Eingabe von vor der Anmeldung wurde wiederhergestellt. Bitte prüfen und speichern.',
      );
    }

    setAuswahlLaedt(true);
    try {
      const meldungen = await trpc.restaufwand.historie.query({ workPackageId: gewaehlt.id });
      if (laedtFuer.current === gewaehlt.id) setHistorie(meldungen);
    } catch (error) {
      if (laedtFuer.current === gewaehlt.id) ladefehlerZeigen(error);
    } finally {
      if (laedtFuer.current === gewaehlt.id) setAuswahlLaedt(false);
    }
  }

  /**
   * Laedt die Historie nach einem Konflikt neu (B5). Waehrenddessen ist alles
   * gesperrt, sonst koennte eine neue Meldung gespeichert werden und die spaete
   * Antwort sie aus der Anzeige verdraengen (Review PR #84, N1).
   */
  async function historieNeuLaden(workPackageId: string): Promise<void> {
    setAuswahlLaedt(true);
    try {
      const meldungen = await trpc.restaufwand.historie.query({ workPackageId });
      if (laedtFuer.current === workPackageId) setHistorie(meldungen);
    } catch (error) {
      if (laedtFuer.current === workPackageId) ladefehlerZeigen(error);
    } finally {
      if (laedtFuer.current === workPackageId) setAuswahlLaedt(false);
    }
  }

  function fehlerZeigen(error: unknown, workPackageId: string): void {
    const befund = klassifiziereFehler(error, { schreibzugriff: true });
    const grund = trpcRestaufwandGrund(error);

    if (befund.art === 'abgebrochen' && !befund.moeglicherweiseAusgefuehrt) return;

    if (grund === 'rueckfrage_fertig_mit_rest') {
      setRueckfrage(true);
      return;
    }

    if (befund.neuAnmelden) {
      setAnmeldung(
        entwuerfe.merken(entwurfsSchluessel(workPackageId), eingabe) ? 'mitEntwurf' : 'ohneEntwurf',
      );
    }

    if (befund.art === 'zugriff') setZugriffVerweigert(true);

    // Dieselbe Id mit anderem Inhalt (E12): die erste Meldung ist gespeichert.
    // Eine neue Id, damit die geaenderte Eingabe als neue Meldung gehen kann —
    // und die Historie neu, damit die gespeicherte Meldung zu sehen ist und die
    // Kommentarpflicht gegen sie rechnet (Review PR #84, B5).
    if (befund.art === 'konflikt') {
      meldungsId.current = neueId();
      void historieNeuLaden(workPackageId);
    }

    setFehler(
      grund !== null
        ? ABWEISUNG_TEXT[grund]
        : befund.moeglicherweiseAusgefuehrt
          ? `${befund.meldung} Die Meldung könnte bereits gespeichert sein. Ein erneutes Speichern mit unveränderter Eingabe legt nichts doppelt an.`
          : befund.art === 'konflikt'
            ? 'Diese Meldung wurde bereits mit anderen Werten gespeichert. Bitte die Historie prüfen.'
            : [befund.meldung, ...befund.felder.map((feld) => feld.message)].join(' '),
    );
  }

  async function senden(ziel: RestaufwandArbeitspaket, eingang: RemainingEffortReportInput) {
    hinweiseZuruecksetzen();
    setSendet(true);

    try {
      const gespeichert = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('work_package', ziel.id),
        () => trpc.restaufwand.melden.mutate(eingang),
      );

      // Es gilt die Serverantwort; der Stand der Liste entsteht mit derselben
      // Funktion, mit der der Server ihn bildet (E6).
      // Der Widerspruch gilt je Bezugswoche (AC-601-1, E2): der Wert aus der
      // Serverantwort gilt für alle Meldungen derselben Woche.
      const neu = [
        ...(historie ?? [])
          .filter((m) => m.id !== gespeichert.id)
          .map((m) =>
            compareIsoWeeks(m.bezugswoche, gespeichert.bezugswoche) === 0
              ? { ...m, widerspruch: gespeichert.widerspruch }
              : m,
          ),
        gespeichert,
      ];
      setHistorie(neu);
      setPakete((vorher) =>
        vorher === null
          ? vorher
          : vorher.map((ap) => {
              if (ap.id !== ziel.id) return ap;
              const stand = aktuelleMeldung(neu);
              return stand === null
                ? ap
                : {
                    ...ap,
                    stand: {
                      restHours: stand.restHours,
                      fertigstellungsgrad: stand.fertigstellungsgrad,
                      bezugswoche: stand.bezugswoche,
                      gemeldetAm: stand.gemeldetAm,
                      drift: stand.drift,
                      widerspruch: stand.widerspruch,
                    },
                  };
            }),
      );
      entwuerfe.vergessen(entwurfsSchluessel(ziel.id));
      meldungsId.current = neueId();
      setEingabe({ ...leereEingabe(heute), bezugswoche: eingang.bezugswoche });
      setMeldung(
        gespeichert.widerspruch
          ? 'Gespeichert. Für diese Woche liegt eine abweichende Meldung eines anderen Kontos vor – bitte abstimmen.'
          : gespeichert.drift
            ? 'Gespeichert. Der Restaufwand liegt über dem Plan und ist als Drift markiert.'
            : 'Gespeichert.',
      );
    } catch (error) {
      fehlerZeigen(error, ziel.id);
    } finally {
      setSendet(false);
    }
  }

  function speichern(bestaetigt: boolean): void {
    if (paket === null || historie === null || gesperrt) return;

    const stand = hinweise(eingabe, historie, paket.planHours);

    // AK 5: die Rueckfrage zuerst im Browser; der Server prueft dasselbe.
    if (!bestaetigt && stand.rueckfrage) {
      setRueckfrage(true);
      return;
    }

    const eingang = zurMeldung(meldungsId.current, paket.id, eingabe, bestaetigt);
    if (eingang !== null) void senden(paket, eingang);
  }

  const kopfzeile = (
    <RestaufwandKopfzeile
      geladen={projekte !== null}
      laedt={laedt}
      ladefehler={ladefehler}
      zugriffGesperrt={zugriffGesperrt}
      anmeldeUrl={anmeldeUrl}
      onOeffnen={() => {
        void oeffnen();
      }}
    />
  );

  return (
    <section>
      <h2>Restaufwand</h2>
      {kopfzeile}

      {projekte !== null && !mitarbeiterVerknuepft && projekte.length === 0 ? (
        <p>
          Ihr Konto ist noch keiner Person zugeordnet. Bitte wenden Sie sich an die
          Systemadministration; erst danach erscheinen hier Ihre Arbeitspakete.
        </p>
      ) : null}
      {projekte === null || (!mitarbeiterVerknuepft && projekte.length === 0) ? null : (
        <ProjektAuswahl
          projekte={projekteGefiltert(projekte, suche)}
          gesamt={projekte.length}
          mandantenweit={mandantenweit}
          suche={suche}
          gewaehlt={projekt?.id ?? null}
          gesperrt={gesperrt}
          onSuche={setSuche}
          onWaehlen={(gewaehlt) => {
            void projektWaehlen(gewaehlt);
          }}
        />
      )}

      {auswahlFehler === null ? null : (
        <p role="alert">
          {[auswahlFehler.meldung, ...auswahlFehler.felder.map((feld) => feld.message)].join(' ')}
        </p>
      )}

      {projekt === null || pakete === null ? null : (
        <ArbeitspaketListe
          projekt={projekt}
          pakete={pakete}
          gewaehlt={paket?.id ?? null}
          gesperrt={gesperrt}
          onWaehlen={(gewaehlt) => {
            void paketWaehlen(gewaehlt);
          }}
        />
      )}

      <p role="status">{meldung}</p>
      {fehler === null ? null : <p role="alert">{fehler}</p>}
      {anmeldung === 'keine' ? null : (
        <p>
          <a href={anmeldeUrl}>
            {anmeldung === 'mitEntwurf'
              ? 'Neu anmelden – Ihre Eingabe bleibt erhalten'
              : 'Neu anmelden – Ihre Eingabe konnte nicht gesichert werden; bitte vorher notieren'}
          </a>
        </p>
      )}

      {paket === null || projekt === null ? null : (
        <>
          {projekt.meldbar ? (
            <MeldeFormular
              paket={paket}
              eingabe={eingabe}
              hinweise={hinweise(eingabe, historie ?? [], paket.planHours)}
              wochen={wochen}
              sendet={sendet}
              gesperrt={gesperrt || historie === null}
              rueckfrage={rueckfrage}
              onAendern={(neu) => {
                setEingabe(neu);
                setRueckfrage(false);
                setMeldung(null);
              }}
              onSpeichern={() => {
                speichern(false);
              }}
              onBestaetigen={() => {
                speichern(true);
              }}
              onRueckfrageAbbrechen={() => {
                setRueckfrage(false);
              }}
            />
          ) : (
            <p>Das Projekt ist nicht beauftragt; die Meldungen sind nur lesbar.</p>
          )}
          <Historie paket={paket} meldungen={historie} laedt={auswahlLaedt} />
        </>
      )}
    </section>
  );
}
